import {
  ChangeEvent,
  CSSProperties,
  JSX,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSetState } from '@gilbarbara/hooks';
import { Draggable } from '@hello-pangea/dnd';
import { Button } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock,
  Edit2,
  Save,
  Trash2,
} from 'lucide-react';

import * as tasksApi from '~/services/api/tasks';
import { logger } from '~/utils/logger';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { PromptIcon } from '~/components/Icons';
import { Tooltip } from '~/components/Tooltip';
import { PromptModal } from '~/pages/Projects/PromptModal';

import type { Subtask, TaskProps } from '~/types';

import { SubtaskList } from './SubtaskList';

interface TaskCardProps {
  task: TaskProps;
  index: number;
  onSelect: () => void;
  onUpdate: (taskId: string, updates: Partial<TaskProps>) => void;
  onDelete: (taskId: string) => void;
  onSubtaskAdd: (taskId: string) => void;
  onSubtaskUpdate: (
    taskId: string,
    subtaskId: string,
    updates: Partial<Omit<Subtask, 'id' | 'taskId' | 'createdAt' | 'updatedAt'>>,
  ) => void;
  onSubtaskDelete: (taskId: string, subtaskId: string) => void;
  isLoadingSubtasks?: boolean;
  subtasksError?: Error | null;
}

interface TaskCardState {
  unsavedFields: {
    description: boolean;
    title: boolean;
  };
  errors: ValidationErrors;
  isSelected: boolean;
  localDescription: string;
  localTitle: string;
  showDeleteConfirmation: boolean;
  showPromptModal: boolean;
  showSubtaskDeleteConfirmation: string | null;
}

interface ValidationErrors {
  title?: string;
  description?: string;
}

export function TaskCard(props: TaskCardProps): JSX.Element {
  const {
    index,
    isLoadingSubtasks = false,
    onDelete,
    onSelect,
    onSubtaskAdd,
    onSubtaskDelete,
    onSubtaskUpdate,
    onUpdate,
    subtasksError = null,
    task,
  } = props;
  const [
    {
      errors,
      isSelected,
      localDescription,
      localTitle,
      showDeleteConfirmation,
      showPromptModal,
      showSubtaskDeleteConfirmation,
      unsavedFields,
    },
    setState,
  ] = useSetState<TaskCardState>({
    errors: {},
    isSelected: false,
    localDescription: task.description || '',
    localTitle: task.id.startsWith('temp-') ? '' : task.title,
    showDeleteConfirmation: false,
    showPromptModal: false,
    showSubtaskDeleteConfirmation: null,
    unsavedFields: {
      description: false,
      title: false,
    },
  });
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Lazy-load task prompt when lightbulb modal is opened
  // Pattern: docs/design-patterns/frontend/api-client.md
  const {
    data: fetchedTask,
    error: promptError,
    isLoading: isLoadingPrompt,
  } = useQuery({
    enabled: showPromptModal,
    queryFn: () => tasksApi.getTask(task.projectId, task.id),
    queryKey: ['task', task.projectId, task.id],
    staleTime: 30000,
  });

  const hasUnsavedChanges = useCallback(() => {
    const titleChanged = localTitle !== task.title || localTitle.trim() === '';
    const descriptionChanged = localDescription !== (task.description || '');
    return titleChanged || descriptionChanged;
  }, [localTitle, localDescription, task.title, task.description]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();

        if (hasUnsavedChanges()) {
          setState({
            unsavedFields: {
              title: localTitle !== task.title || localTitle.trim() === '',
              description: localDescription !== (task.description || ''),
            },
          });

          // setTimeout(() => {
          //   setBlinkFields({ title: false, description: false });
          // }, 820);
        } else {
          onUpdate(task.id, { isEditing: false });
        }
      }
    },
    [
      hasUnsavedChanges,
      localTitle,
      localDescription,
      onUpdate,
      setState,
      task.title,
      task.description,
      task.id,
    ],
  );

  const validateTask = useCallback(() => {
    const validationErrors: ValidationErrors = {};
    if (!localTitle.trim()) {
      validationErrors.title = 'Title is required';
    }
    setState({ errors: validationErrors });
    return Object.keys(validationErrors).length === 0;
  }, [localTitle, setState]);

  const handleDelete = useCallback(() => {
    // For temporary tasks, delete immediately without confirmation
    if (task.id.startsWith('temp-')) {
      onDelete(task.id);
    } else {
      setState({ showDeleteConfirmation: true });
    }
  }, [task.id, onDelete, setState]);

  const handleSave = useCallback(() => {
    if (validateTask()) {
      onUpdate(task.id, {
        title: localTitle,
        description: localDescription || undefined,
        isEditing: false,
      });
    }
  }, [validateTask, onUpdate, task.id, localTitle, localDescription]);

  const getStatusIcon = (status: TaskProps['status']): JSX.Element => {
    switch (status) {
      case 'TODO':
        return <Clock className="w-5 h-5 text-foreground-600 dark:text-muted" />;
      case 'IN_PROGRESS':
        return <Activity className="w-5 h-5 text-primary dark:text-foreground" />;
      case 'DONE':
        return <CheckCircle2 className="w-5 h-5 text-success dark:text-success" />;
      default:
        return <Circle className="w-5 h-5 text-foreground-600 dark:text-muted" />;
    }
  };

  const getPriorityColor = (priority: TaskProps['priority']): string => {
    switch (priority) {
      case 'LOW':
        return 'bg-surface-elevated dark:bg-background/30 text-foreground dark:text-muted';
      case 'MEDIUM':
        return 'bg-warning/10 dark:bg-warning/10 text-warning dark:text-warning';
      case 'HIGH':
        return 'bg-error/10 dark:bg-error/10 text-error dark:text-error';
      default:
        return 'bg-surface-elevated dark:bg-background/30 text-foreground dark:text-muted';
    }
  };

  useEffect(() => {
    logger.debug(
      {
        component: 'TaskCard',
        taskId: task.id,
      },
      'Task subtasks updated',
      {
        subtasks: task.subtasks.map(s => ({
          id: s.id,
          isEditing: s.isEditing,
          isTemp: s.id.startsWith('temp-'),
        })),
      },
    );
  }, [task.subtasks, task.id]);

  // Handle focus when entering edit mode
  useEffect(() => {
    if (task.isEditing && titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 0);
    }
  }, [task.isEditing]);

  // Memoize the SubtaskList component to prevent unnecessary re-renders
  const MemoizedSubtaskList = useMemo(
    () => (
      <SubtaskList
        taskId={task.id}
        subtasks={task.subtasks}
        onSubtaskAdd={onSubtaskAdd}
        onSubtaskUpdate={onSubtaskUpdate}
        onSubtaskDelete={onSubtaskDelete}
        isLoading={isLoadingSubtasks}
        error={subtasksError}
      />
    ),
    [
      task.id,
      task.subtasks,
      onSubtaskAdd,
      onSubtaskUpdate,
      onSubtaskDelete,
      isLoadingSubtasks,
      subtasksError,
    ],
  );

  // Handle select click
  const handleClickSelect = useCallback(() => {
    onSelect();
  }, [onSelect]);

  // Handle edit button click
  const handleClickEdit = useCallback(() => {
    onUpdate(task.id, { isEditing: true });
  }, [onUpdate, task.id]);

  // Handle priority change
  const handleChangePriority = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      e.stopPropagation();
      onUpdate(task.id, {
        priority: e.target.value,
      });
    },
    [onUpdate, task.id],
  );

  // Handle click stop propagation
  const handleClickInputs = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Handle title change
  const handleChangeTitle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setState({ localTitle: e.target.value });
    },
    [setState],
  );

  // Handle description change
  const handleChangeDescription = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setState({ localDescription: e.target.value });
    },
    [setState],
  );

  // Handle save button click
  const handleClickSave = useCallback(() => {
    handleSave();
  }, [handleSave]);

  // Handle delete button click
  const handleClickDelete = useCallback(() => {
    handleDelete();
  }, [handleDelete]);

  // Handle confirm delete
  const handleDeleteConfirmation = useCallback(() => {
    onDelete(task.id);
    setState({ showDeleteConfirmation: false });
  }, [onDelete, task.id, setState]);

  // Handle cancel delete
  const handleDeleteCancel = useCallback(() => {
    setState({ showDeleteConfirmation: false });
  }, [setState]);

  // Handle confirm subtask delete
  const handleSubtaskDeleteConfirmation = useCallback(() => {
    if (showSubtaskDeleteConfirmation) {
      onSubtaskDelete(task.id, showSubtaskDeleteConfirmation);
      setState({ showSubtaskDeleteConfirmation: null });
    }
  }, [onSubtaskDelete, task.id, showSubtaskDeleteConfirmation, setState]);

  // Handle cancel subtask delete
  const handleSubtaskDeleteCancel = useCallback(() => {
    setState({ showSubtaskDeleteConfirmation: null });
  }, [setState]);

  // Handle prompt icon click
  const handleClickPrompt = useCallback(() => {
    setState({ showPromptModal: true });
  }, [setState]);

  // Handle prompt dialog close
  const handleClosePromptViewer = useCallback(() => {
    setState({ showPromptModal: false });
  }, [setState]);

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <>
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            aria-label="Task Card"
            aria-selected={isSelected}
            className={`p-4 mb-3 rounded-lg transition-all w-full cursor-grab bg-surface-elevated dark:bg-surface-elevated ${
              snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/10' : 'shadow-sm hover:shadow-md'
            } ${
              task.isEditing
                ? 'ring-2 ring-primary dark:ring-primary'
                : 'border border-default hover:border-primary dark:hover:border-primary'
            }`}
            onClick={handleClickSelect}
            onKeyDown={e => {
              // Don't handle keyboard events if focus is on an input element
              const target = e.target as HTMLElement;
              if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                return;
              }

              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setState({ isSelected: true });
                handleClickSelect();
              }
            }}
            role="option"
            tabIndex={0}
          >
            <div className="flex flex-col gap-3 w-full">
              <div className="flex flex-wrap items-center gap-3 w-full">
                {getStatusIcon(task.status)}
                {task.isEditing ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={localTitle}
                    onChange={handleChangeTitle}
                    onKeyDown={handleKeyDown}
                    style={
                      {
                        '--border-color': errors.title
                          ? 'var(--color-error)'
                          : 'var(--color-border)',
                        width: 'calc(100% - 36px)',
                      } as CSSProperties
                    }
                    className={`flex-1 px-3 py-1.5 border rounded-md bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 ${
                      errors.title ? 'border-error' : 'border-default dark:border-default'
                    } ${unsavedFields.title ? 'animate-unsaved-changes' : ''}`}
                    placeholder="Task title"
                    onClick={handleClickInputs}
                  />
                ) : (
                  <div className="flex items-center gap-3 flex-1 min-w-0 max-w-full">
                    <h3 className="flex-1 min-w-0 wrap-break-word text-base font-medium text-foreground">
                      {task.title}
                    </h3>
                    {!task.isEditing && (
                      <>
                        <Tooltip content="Edit prompt">
                          <Button
                            aria-label="Edit prompt"
                            className="shrink-0"
                            isIconOnly
                            onPress={handleClickPrompt}
                            size="sm"
                            variant="light"
                          >
                            <PromptIcon
                              className={`w-4 h-4 ${
                                task.prompt || task.hasPrompt
                                  ? 'text-warning dark:text-warning'
                                  : 'text-muted hover:text-warning dark:hover:text-warning'
                              }`}
                            />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Edit task">
                          <Button
                            aria-label="Edit task"
                            className="shrink-0"
                            isIconOnly
                            onPress={handleClickEdit}
                            size="sm"
                            variant="light"
                          >
                            <Edit2 className="w-4 h-4 text-muted hover:text-primary dark:hover:text-foreground" />
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Priority under title - only show when not editing */}
              {!task.isEditing && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground-600 dark:text-muted">Priority:</span>
                  <select
                    value={task.priority}
                    onChange={handleChangePriority}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                      task.priority,
                    )} dark:border dark:border-default cursor-pointer hover:ring-2 hover:ring-border dark:hover:ring-border transition-shadow`}
                    onClick={handleClickInputs}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
              )}

              {task.isEditing && (
                <div className="flex items-center justify-between gap-4 w-full">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground-600 dark:text-muted">Priority:</span>
                    <select
                      value={task.priority}
                      onChange={handleChangePriority}
                      className={`px-3 py-1 rounded-full text-sm font-medium w-32 ${getPriorityColor(
                        task.priority,
                      )} dark:border dark:border-default cursor-pointer hover:ring-2 hover:ring-border dark:hover:ring-border`}
                      onClick={handleClickInputs}
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      aria-label="Save Task"
                      color="success"
                      isIconOnly
                      onPress={handleClickSave}
                      size="sm"
                      variant="light"
                    >
                      <Save className="w-5 h-5 text-success dark:text-success" />
                    </Button>
                    <Button
                      aria-label="Delete Task"
                      isIconOnly
                      color="danger"
                      onPress={handleClickDelete}
                      size="sm"
                      variant="light"
                    >
                      <Trash2 className="w-5 h-5 text-error dark:text-error" />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {task.isEditing ? (
              <textarea
                value={localDescription}
                onChange={handleChangeDescription}
                onKeyDown={handleKeyDown}
                style={
                  {
                    '--border-color': 'var(--color-border)',
                    width: '100%',
                  } as CSSProperties
                }
                className={`w-full px-2 py-1 border border-default rounded mb-3 mt-3 min-h-[60px] bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary/50 ${
                  unsavedFields.description ? 'animate-unsaved-changes' : ''
                }`}
                placeholder="Task description (optional)"
                onClick={handleClickInputs}
              />
            ) : (
              task.description && (
                <div className="mt-3 mb-3">
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setIsDescriptionExpanded(prev => !prev);
                    }}
                    className="flex items-center gap-1 text-sm text-foreground-600 dark:text-muted hover:text-foreground transition-colors"
                  >
                    <ChevronRight
                      className={`w-4 h-4 transition-transform duration-150 ${isDescriptionExpanded ? 'rotate-90' : ''}`}
                    />
                    Description
                  </button>
                  {isDescriptionExpanded && (
                    <p className="text-foreground-600 dark:text-muted text-sm mt-1.5 pl-5 wrap-break-word">
                      {task.description}
                    </p>
                  )}
                </div>
              )
            )}

            <div className="mt-4">{MemoizedSubtaskList}</div>

            {errors.title && (
              <p className="text-error dark:text-error text-sm mt-2">{errors.title}</p>
            )}

            {showDeleteConfirmation && (
              <ConfirmationDialog
                content={
                  <>
                    <p>Are you sure you want to delete this task?</p>
                    <p>This action cannot be undone.</p>
                  </>
                }
                isOpen={showDeleteConfirmation}
                onConfirm={handleDeleteConfirmation}
                onClose={handleDeleteCancel}
                title="Delete Task"
              />
            )}
            {showSubtaskDeleteConfirmation && (
              <ConfirmationDialog
                content={
                  <>
                    <p>Are you sure you want to delete this subtask?</p>
                    <p>This action cannot be undone.</p>
                  </>
                }
                isOpen={!!showSubtaskDeleteConfirmation}
                onConfirm={handleSubtaskDeleteConfirmation}
                onClose={handleSubtaskDeleteCancel}
                title="Delete Subtask"
              />
            )}
            {showPromptModal && (
              <PromptModal
                isOpen={showPromptModal}
                key={task.id}
                loadError={promptError}
                isLoading={isLoadingPrompt}
                prompt={fetchedTask?.prompt ?? task.prompt}
                projectId={task.projectId}
                title={`Prompt: ${task.title}`}
                onCancel={handleClosePromptViewer}
                onSave={promptText => {
                  onUpdate(task.id, { prompt: promptText });
                  setState({ showPromptModal: false });
                }}
              />
            )}
          </div>
        </>
      )}
    </Draggable>
  );
}
